export interface BackendIdentity {
  name: string;
  url: string;
}

interface BackendAuthToken extends BackendIdentity {
  token: string;
}

const BACKEND_TOKENS_STORAGE_KEY = 'admin-backend-auth-tokens';
const DISABLED_BACKENDS_STORAGE_KEY = 'admin-disabled-backends';
const PRIMARY_AUTH_TOKEN_STORAGE_KEY = 'primary-admin-auth-token';
const PRIMARY_AUTH_BACKEND_STORAGE_KEY = 'primary-admin-auth-backend';

function readArray<T>(key: string): T[] {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]');

    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function getConfiguredBackends(): BackendIdentity[] {
  const raw = process.env.REACT_APP_BACKENDS;

  if (!raw) {
    return [];
  }

  try {
    let value = raw.trim();

    if (
      (value.startsWith("'") && value.endsWith("'")) ||
      (value.startsWith('"') && value.endsWith('"'))
    ) {
      value = value.slice(1, -1);
    }

    const parsed = JSON.parse(value);

    return Array.isArray(parsed)
      ? parsed.filter((backend) => Boolean(backend?.name && backend?.url))
      : [];
  } catch {
    return [];
  }
}

export function getDisabledBackends(): BackendIdentity[] {
  return readArray<BackendIdentity | string>(DISABLED_BACKENDS_STORAGE_KEY)
    .map((backend) =>
      typeof backend === 'string'
        ? { name: backend, url: '' }
        : backend,
    )
    .filter((backend) => Boolean(backend?.name));
}

export function isBackendDisabled(name?: string): boolean {
  if (!name) return false;

  return getDisabledBackends().some((backend) => backend.name === name);
}

export function isBackendUrlDisabled(url?: string): boolean {
  if (!url || !/^https?:\/\//i.test(url)) return false;

  return getDisabledBackends().some((backend) => {
    if (!backend.url) return false;

    return url.startsWith(backend.url.replace(/\/+$/, ''));
  });
}

export function filterEnabledBackends<T extends { name: string }>(backends: T[]): T[] {
  const configuredNames = new Set(getConfiguredBackends().map((backend) => backend.name));
  const disabledNames = new Set(getDisabledBackends().map((backend) => backend.name));

  return backends.filter(
    (backend) => configuredNames.has(backend.name) && !disabledNames.has(backend.name),
  );
}

export function syncStoredBackendsWithConfiguration(): void {
  const storedBackends = readArray<BackendAuthToken>(BACKEND_TOKENS_STORAGE_KEY);
  const activeBackends = filterEnabledBackends(storedBackends);

  if (JSON.stringify(storedBackends) !== JSON.stringify(activeBackends)) {
    localStorage.setItem(BACKEND_TOKENS_STORAGE_KEY, JSON.stringify(activeBackends));
  }

  try {
    const primaryBackend = JSON.parse(localStorage.getItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY) || 'null');
    const activePrimaryBackend = activeBackends.find((backend) => backend.name === primaryBackend?.name);

    if (activePrimaryBackend) {
      return;
    }

    const fallbackBackend = activeBackends[0];

    if (fallbackBackend) {
      localStorage.setItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY, JSON.stringify(fallbackBackend));
      localStorage.setItem(PRIMARY_AUTH_TOKEN_STORAGE_KEY, fallbackBackend.token);
    } else {
      localStorage.removeItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY);
      localStorage.removeItem(PRIMARY_AUTH_TOKEN_STORAGE_KEY);
    }
  } catch {
    localStorage.removeItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY);
    localStorage.removeItem(PRIMARY_AUTH_TOKEN_STORAGE_KEY);
  }
}

export function disableBackend(backend: BackendIdentity): void {
  const disabledBackends = getDisabledBackends().filter((item) => item.name !== backend.name);

  localStorage.setItem(
    DISABLED_BACKENDS_STORAGE_KEY,
    JSON.stringify([...disabledBackends, backend]),
  );

  const activeBackends = readArray<BackendAuthToken>(BACKEND_TOKENS_STORAGE_KEY).filter(
    (item) => item.name !== backend.name,
  );

  localStorage.setItem(BACKEND_TOKENS_STORAGE_KEY, JSON.stringify(activeBackends));

  try {
    const primaryBackend = JSON.parse(localStorage.getItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY) || 'null');

    if (primaryBackend?.name === backend.name) {
      const fallbackBackend = activeBackends[0];

      if (fallbackBackend) {
        localStorage.setItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY, JSON.stringify(fallbackBackend));
        localStorage.setItem(PRIMARY_AUTH_TOKEN_STORAGE_KEY, fallbackBackend.token);
      } else {
        localStorage.removeItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY);
        localStorage.removeItem(PRIMARY_AUTH_TOKEN_STORAGE_KEY);
      }
    }
  } catch {
    localStorage.removeItem(PRIMARY_AUTH_BACKEND_STORAGE_KEY);
    localStorage.removeItem(PRIMARY_AUTH_TOKEN_STORAGE_KEY);
  }
}

export function enableBackend(name: string): void {
  const disabledBackends = getDisabledBackends().filter((backend) => backend.name !== name);

  localStorage.setItem(DISABLED_BACKENDS_STORAGE_KEY, JSON.stringify(disabledBackends));
}
